import { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Skeleton } from "./components/ui/skeleton";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PublishProvider } from './contexts/PublishContext';
import { ThemeToggle } from './components/ThemeToggle';
import { AppLayout } from './components/AppLayout';
import { LoginModal } from './components/LoginModal';
import { OutsourcedShowsScreen } from './pages/OutsourcedShowsScreen';
import { AllShowsScreen } from './pages/AllShowsScreen';
import Dashboard from './pages/Dashboard';
import Publish from './pages/Publish';
import InternalDetails from './pages/InternalDetails';
import PromoDetails from './pages/PromoDetails';
import DubbingDetails from './pages/DubbingDetails';
import PublishContent from './pages/PublishContent';
import PublishPreview from './pages/PublishPreview';
import WhitelistCreate from './pages/WhitelistCreate';
import FloatingIconManager from './pages/FloatingIconManager';
import FloatingIconForm from './pages/FloatingIconForm';
import TabNudgeManager from './pages/TabNudgeManager';
import TabNudgeForm from './pages/TabNudgeForm';
import { RestrictedAccess } from './pages/RestrictedAccess';
import { useUserShows } from './hooks/useUserShows';
import { getShowThumbnailUrl } from './utils/thumbnailUtils';
import { Toaster } from 'sonner';
import styles from './styles/App.module.css';
import './index.css';

const communityProjects = [
  {
    title: "Cyberpunk Detective",
    tag: "Comic",
    image: "https://images.unsplash.com/photo-1599420186946-7b6fb4e297f0?w=800&q=80"
  },
  {
    title: "Dragon's Quest Character Design",
    tag: "Character",
    image: "https://images.unsplash.com/photo-1560942485-b2a11cc13456?w=800&q=80"
  },
  {
    title: "Space Warriors",
    tag: "Video",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80"
  },
  {
    title: "Urban Tales Comic Series",
    tag: "Comic",
    image: "https://images.unsplash.com/photo-1555661059-7e755c1c3c1d?w=800&q=80"
  },
  {
    title: "Neon City Nights",
    tag: "Image",
    image: "https://images.unsplash.com/photo-1563089145-599997674d42?w=800&q=80"
  },
  {
    title: "Mythical Creatures Animation",
    tag: "Video",
    image: "https://images.unsplash.com/photo-1534447677768-be436bb09401?w=800&q=80"
  }
];


function Home() {
  const navigate = useNavigate();
  const { isAuth, loading: authLoading } = useAuth();
  const { shows, loading: showsLoading, error: showsError } = useUserShows(5);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Show login modal by default when user is not authenticated
  useEffect(() => {
    if (!authLoading && !isAuth) {
      setShowLoginModal(true);
    }
  }, [authLoading, isAuth]);

  const handleCreateNew = () => {
    if (isAuth) {
      navigate('/publish');
    } else {
      // Could show login modal or redirect to login
      console.log('User needs to login first');
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentWrapper}>
        {/* Navigation */}
        <div className={styles.navigation}>
          <div className={styles.navContent}>
            <h1 className={styles.navTitle}>Home</h1>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              {isAuth && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/publish')}
                  className={styles.navButton}
                >
                  Publish
                </Button>
              )}
            </div>
          </div>
        </div>

      {/* Banner */}
      <div className={styles.banner}>
        <div className={styles.bannerContent}>
          <img 
            src="https://images.unsplash.com/photo-1599420186946-7b6fb4e297f0?w=1200&q=80" 
            alt="Banner" 
            className={styles.bannerImage}
          />
          <div className={styles.bannerOverlay}>
            <div className={styles.bannerText}>
              <h1 className={styles.bannerTitle}>Welcome to Inhouse - CMS</h1>
              <p className={styles.bannerDescription}>Buckleup to deliever leading content</p>
              <Button
                variant="default"
                onClick={handleCreateNew}
                className={styles.createButton}
              >
                Create New
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Projects */}
      <div className="flex items-center justify-between px-4 mb-6 mt-12">
        <h2 className={styles.communityTitle}>Your Projects</h2>
        {isAuth && (
          <Button 
            variant="ghost" 
            className="text-sm font-normal h-7"
            onClick={() => navigate('/all-shows')}
          >
            View All
            <ArrowRight className="ml-1.5 h-3 w-3" />
          </Button>
        )}
      </div>

      <div className="px-4">
        {/* Loading Skeleton */}
        {showsLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={`skeleton-${index}`} className={styles.projectCard}>
                <Skeleton className="w-full aspect-square rounded-lg" />
                <div className="pt-2">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* User Shows - Show when authenticated and has shows */}
        {isAuth && !showsLoading && !showsError && shows.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {shows.slice(0, 5).map((show) => (
              <div 
                key={show.id}
                className={styles.projectCard}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Show card clicked:', show.id);
                  if (show.id) {
                    navigate(`/publish/${show.id}`);
                  } else {
                    console.error('No show ID available');
                    navigate('/publish');
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-shadow bg-card">
                  <CardContent className="p-0">
                    <img 
                      src={getShowThumbnailUrl(show)} 
                      alt={show.name} 
                      className={styles.projectImage}
                      draggable={false}
                    />
                  </CardContent>
                </Card>
                <div className={styles.projectInfo}>
                  <h3 className={styles.projectTitle}>{show.name}</h3>
                  <Badge
                    variant="secondary"
                    className="text-xs font-normal px-2 py-0.5 rounded-md shrink-0 text-muted-foreground pointer-events-none"
                  >
                    {show.episodeCount} episodes
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sample projects - Show when not authenticated OR when authenticated but has no shows or error */}
        {!showsLoading && (!isAuth || (isAuth && (showsError || shows.length === 0))) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {communityProjects.map((project, index) => (
              <div 
                key={`popular-${index}`}
                className={styles.projectCard}
              >
                <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-shadow bg-card">
                  <CardContent className="p-0">
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className={styles.projectImage}
                    />
                  </CardContent>
                </Card>
                <div className={styles.projectInfo}>
                  <h3 className={styles.projectTitle}>{project.title}</h3>
                  <Badge
                    variant="secondary"
                    className="text-xs font-normal px-2 py-0.5 rounded-md shrink-0 text-muted-foreground pointer-events-none"
                  >
                    {project.tag}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <button className={styles.footerLink}>FAQ</button>
          <button className={styles.footerLink}>Legal</button>
          <button className={styles.footerLink}>Privacy</button>
          <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>Discord</a>
          <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>Youtube</a>
          <button className={styles.footerLink}>Dashtoon</button>
        </div>
      </footer>

      {/* Login Modal */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  );
}


function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PublishProvider>
            <Toaster
              position="top-center"
              richColors
              expand
              visibleToasts={6}
              offset={16}
              gap={8}
              closeButton={false}
            />
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/home" element={<AppLayout><Home /></AppLayout>} />
              <Route path="/publish" element={<AppLayout><Publish /></AppLayout>} />
              <Route path="/publish/:showId" element={<AppLayout><Publish /></AppLayout>} />
              <Route path="/publish/:showId/internal-details" element={<AppLayout><InternalDetails /></AppLayout>} />
              <Route path="/publish/:showId/promo-details" element={<AppLayout><PromoDetails /></AppLayout>} />
              <Route path="/publish/:showId/dubbing-details" element={<AppLayout><DubbingDetails /></AppLayout>} />
              <Route path="/publish/:showId/thumbnails" element={<AppLayout><PublishContent view="thumbnails" /></AppLayout>} />
              <Route path="/publish/:showId/content" element={<AppLayout><PublishContent view="content" /></AppLayout>} />
              <Route path="/publish/:showId/extras" element={<AppLayout><PublishContent view="extras" /></AppLayout>} />
              <Route path="/publish/:showId/preview" element={<AppLayout><PublishPreview /></AppLayout>} />
              <Route path="/whitelist/create" element={<WhitelistCreate />} />
              <Route path="/restricted-access" element={<RestrictedAccess />} />
              <Route path="/outsourced-shows" element={<AppLayout><OutsourcedShowsScreen /></AppLayout>} />
              <Route path="/all-shows" element={<AppLayout><AllShowsScreen /></AppLayout>} />
              <Route path="/floating-icons" element={<AppLayout><FloatingIconManager /></AppLayout>} />
              <Route path="/floating-icons/create" element={<AppLayout><FloatingIconForm /></AppLayout>} />
              <Route path="/floating-icons/:id/edit" element={<AppLayout><FloatingIconForm /></AppLayout>} />
              <Route path="/tab-nudges" element={<AppLayout><TabNudgeManager /></AppLayout>} />
              <Route path="/tab-nudges/create" element={<AppLayout><TabNudgeForm /></AppLayout>} />
              <Route path="/tab-nudges/:id/edit" element={<AppLayout><TabNudgeForm /></AppLayout>} />
              <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </PublishProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
